import { KImage, KText } from '@/ui-library/components';
import { COLORS } from '@/ui-library/design-system';
import { Flex, Icon, Tooltip } from '@chakra-ui/react';
import moment from 'moment-timezone';
import { useEffect, useState } from 'react';
import { components } from 'react-select';
import { SchInterviewerCalNotConnected } from '@/components/shared/interview-scheduler/SchedulerDetails/InterviewSchedulerForm/Fields/SchInterviewerField/SchInterviewerCalNotConnected';
import { SchInterviewerTimezoneNotSet } from '@/components/shared/interview-scheduler/SchedulerDetails/InterviewSchedulerForm/Fields/SchInterviewerField/SchInterviewerTimezoneNotSet';
import { TIME_ZONE_CONSTANTS } from '@/utils/timeZoneConstants';
import { AlertTriangle } from '@kula-ai/icons/dual-tone/AlertTriangle';

export const SchInterviewerSingleLabel = (props) => {
  const {
    interviewDataRef,
    data,
    registerForUpdates,
    variant,
    SELF_SCHEDULE_VARIANTS,
    onTimezoneClick,
  } = props;
  const { label, avatar, time_zone } = props?.data || {};
  const { value } = data || {};
  const {
    colorCodes,
    userCalendarInfo,
    bookedSlot,
    calendarOwnerUserId,
    calendar_info,
  } = interviewDataRef?.current || {};
  // eslint-disable-next-line no-unsafe-optional-chaining, @typescript-eslint/no-unused-vars -- pre-existing; behavior preserved (extraction move)
  const { light, dark } = colorCodes?.[value] || COLORS?.['pastel-1'];

  const isUserCalendarAvailable =
    userCalendarInfo?.[value]?.isCalendarConnected;

  // Don't show warning if it's the user's primary calendar
  const isCalendarOwner =
    calendarOwnerUserId &&
    value === calendarOwnerUserId &&
    !calendar_info?.is_primary;

  // Check if timezone is missing
  const isTimeZoneNotAvailable = !time_zone;

  const [, forceUpdate] = useState({});

  useEffect(() => {
    const unregisterColorCodes = registerForUpdates('colorCodes', () => {
      forceUpdate({});
    });

    const unregisterBookedSlot = registerForUpdates('bookedSlot', () => {
      forceUpdate({});
    });

    const unregisterUserCalendarInfo = registerForUpdates(
      'userCalendarInfo',
      () => {
        forceUpdate({});
      }
    );

    const unregisterCalendarOwnerUserId = registerForUpdates(
      'calendarOwnerUserId',
      () => {
        forceUpdate({});
      }
    );

    return () => {
      unregisterColorCodes();
      unregisterUserCalendarInfo();
      unregisterBookedSlot();
      unregisterCalendarOwnerUserId();
    };
  }, [registerForUpdates]);

  const timeZone = time_zone; // || findTimeZone(moment.tz.guess())?.name;
  const { abbreviation } = TIME_ZONE_CONSTANTS?.[timeZone] || {};

  const slotTime = timeZone
    ? moment(bookedSlot?.start).tz(timeZone).format('hh:mm A')
    : '';

  return (
    <components.SingleValue {...props}>
      <Flex
        gap="4px"
        alignItems="center"
        width="100%"
        overflow="hidden"
        padding="4px"
        borderRadius="5px"
      >
        {isCalendarOwner && (
          <Tooltip
            shouldWrapChildren
            bg="secondary-950"
            borderRadius="6px"
            p="12px"
            label="As the Outlook calendar owner, this interviewer will not receive a meeting invite to join the interview. They'll receive a notification from Kula instead."
            placement="bottom"
            hasArrow
          >
            <Icon
              as={AlertTriangle}
              w="16px"
              h="14px"
              color="warning-200"
              flexShrink={0}
              position="relative"
              bottom="1px"
              mr="4px"
            />
          </Tooltip>
        )}
        {isTimeZoneNotAvailable &&
          !isCalendarOwner &&
          (variant === SELF_SCHEDULE_VARIANTS.SELF ||
            variant === SELF_SCHEDULE_VARIANTS.BULK_SELF) && (
            <SchInterviewerTimezoneNotSet
              onSetTimezone={() => onTimezoneClick?.()}
            />
          )}
        {isUserCalendarAvailable === false && !isCalendarOwner && (
          <SchInterviewerCalNotConnected />
        )}
        <KImage src={avatar} name={label} size="16px" />
        <Flex gap="4px" alignItems="flex-end" overflow="hidden" flex={1}>
          <KText
            variant="paragraph"
            maxWidth="100%"
            flexShrink={1}
            isTruncated
            fontWeight="450"
            color={isCalendarOwner ? 'pastel-6-dark' : 'secondary-700'}
          >
            {label}
          </KText>
          {bookedSlot?.start && time_zone && slotTime && (
            <Tooltip
              shouldWrapChildren
              flexShrink={0}
              label={'Interview will happen at this time for this interviewer'}
            >
              <KText
                minWidth="fit-content"
                flexShrink={0}
                variant="small_text"
                color={isCalendarOwner ? 'pastel-6-dark' : 'secondary-700'}
              >
                {slotTime} {abbreviation ? `${abbreviation}` : ''}
              </KText>
            </Tooltip>
          )}
        </Flex>
      </Flex>
    </components.SingleValue>
  );
};
